/**
 * Voice Lock Settings — settings panel section for Voice Lock feature.
 *
 * Shows:
 * - Toggle to enable/disable live scoring during voice sessions
 * - Toggle to enable/disable the audio gate (block unrecognized voices)
 * - Model picker dropdown
 * - Threshold slider
 * - Training flow with prep tips, guided prompts, step dots, and audio level
 * - Post-training verification step
 * - Test button to enter test overlay
 * - Clear Profile button (only if enrolled)
 */

import { useState, useEffect, useRef } from 'react';
import type { UseVoiceLockReturn } from '../hooks/useVoiceLock';
import {
  SPEAKER_MODELS,
  THRESHOLD_MIN,
  THRESHOLD_MAX,
  THRESHOLD_STEP,
} from '../hooks/useVoiceLock';
import styles from './Settings.module.css';
import vlStyles from './VoiceLockSettings.module.css';

interface VoiceLockSettingsProps {
  voiceLock: UseVoiceLockReturn;
}

/** Format relative time (e.g., "2m ago", "1h ago") */
function formatTimeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

type TrainingPhase = 'idle' | 'prep' | 'recording' | 'done' | 'verifying' | 'verified' | 'verify-failed';

export function VoiceLockSettings({ voiceLock }: VoiceLockSettingsProps) {
  const {
    gateEnabled,
    isEnrolled,
    isTraining,
    isAdditiveTraining,
    isInitializing,
    trainedAt,
    trainingSampleCount,
    audioLevel,
    trainingComplete,
    trainingError,
    promptedSentences,
    adaptiveScore,
    adaptiveScores,
    selectedModel,
    changeModel,
    threshold,
    setThreshold,
    verificationScore,
    verificationStatus,
    verificationSentence,
    setGateEnabled,
    startTraining,
    startAdditiveTraining,
    stopTraining,
    enterTestMode,
    clearProfile,
  } = voiceLock;

  // Training phase — managed in the component to keep hook clean
  const [trainingPhase, setTrainingPhase] = useState<TrainingPhase>('idle');
  // Whether the prep screen is for additive training (true) or fresh training (false)
  const [pendingAdditive, setPendingAdditive] = useState(false);
  // Brief "Got it!" flash per sample
  const [showCaptured, setShowCaptured] = useState(false);
  // Brief success message after training completes
  const [showSuccess, setShowSuccess] = useState(false);
  // (pendingModelKey removed — model switching is no longer destructive)
  // Track previous sample count to detect new captures
  const prevSampleCountRef = useRef(0);

  // Dynamic required samples based on training mode
  const requiredSamples = isAdditiveTraining ? 3 : 5;

  // Detect when a new sample is captured → flash "Got it!"
  useEffect(() => {
    if (trainingSampleCount > prevSampleCountRef.current && trainingSampleCount > 0 && !trainingComplete) {
      setShowCaptured(true);
      const timer = setTimeout(() => setShowCaptured(false), 800);
      prevSampleCountRef.current = trainingSampleCount;
      return () => clearTimeout(timer);
    }
    prevSampleCountRef.current = trainingSampleCount;
  }, [trainingSampleCount, trainingComplete]);

  // Transition to 'done' phase when training completes
  useEffect(() => {
    if (trainingComplete && trainingPhase === 'recording') {
      setTrainingPhase('done');
    }
  }, [trainingComplete, trainingPhase]);

  // Transition to verification phases based on hook state
  useEffect(() => {
    if (verificationStatus === 'listening' && trainingPhase !== 'verifying') {
      setTrainingPhase('verifying');
    } else if (verificationStatus === 'passed' && trainingPhase === 'verifying') {
      setTrainingPhase('verified');
    } else if (verificationStatus === 'failed' && trainingPhase === 'verifying') {
      setTrainingPhase('verify-failed');
    }
  }, [verificationStatus, trainingPhase]);

  // Reset phase when training stops (from hook side) — but respect verification phases
  useEffect(() => {
    if (!isTraining && trainingPhase === 'recording') {
      // Training stopped by hook (enrollment done, verification starting)
      // Don't reset — let verification phase take over
    }
  }, [isTraining, trainingPhase]);

  // Show success message after verified
  useEffect(() => {
    if (trainingPhase === 'verified') {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setTrainingPhase('idle');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [trainingPhase]);

  /** User clicks "Train Voice" or "Re-train" → show prep screen for fresh training */
  const handleTrainClick = () => {
    setPendingAdditive(false);
    setTrainingPhase('prep');
    prevSampleCountRef.current = 0;
  };

  /** User clicks "Add Training" → show prep screen for additive training */
  const handleAdditiveTrainClick = () => {
    setPendingAdditive(true);
    setTrainingPhase('prep');
    prevSampleCountRef.current = 0;
  };

  /** User clicks "Start Recording" on prep screen → actually start training */
  const handleStartRecording = () => {
    setTrainingPhase('recording');
    if (pendingAdditive) {
      startAdditiveTraining();
      setPendingAdditive(false);
    } else {
      startTraining();
    }
  };

  /** Cancel from prep screen without starting */
  const handleCancelPrep = () => {
    setTrainingPhase('idle');
    setPendingAdditive(false);
  };

  /** Start additive training from verify-failed state */
  const handleAdditiveTraining = () => {
    setTrainingPhase('recording');
    prevSampleCountRef.current = 0;
    startAdditiveTraining();
  };

  /** Full retrain from verify-failed state */
  const handleFullRetrain = () => {
    setTrainingPhase('idle');
    handleTrainClick();
  };

  /** Handle model selection from dropdown — no confirmation needed, profiles are per-model */
  const handleModelSelect = (key: string) => {
    if (key === selectedModel) return;
    void changeModel(key);
  };

  return (
    <div className={styles.section}>
      <label className={styles.label}>Voice Lock</label>

      {/* Audio gate toggle — controls voice-based audio blocking */}
      <label className={styles.toggle}>
        <span className={styles.toggleLabel}>🔒 Audio gate</span>
        <div
          className={`${styles.toggleTrack} ${gateEnabled ? styles.toggleActive : ''}`}
          onClick={() => setGateEnabled(!gateEnabled)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setGateEnabled(!gateEnabled);
            }
          }}
          role="switch"
          aria-checked={gateEnabled}
          tabIndex={0}
        >
          <div className={styles.toggleThumb} />
        </div>
      </label>
      {gateEnabled && !isEnrolled && (
        <span className={vlStyles.gateHint}>
          Gate is on but no voice profile trained — all voices will pass through
        </span>
      )}
      {gateEnabled && isEnrolled && (
        <span className={vlStyles.gateHint} style={{ color: '#888' }}>
          Blocking unrecognized voices
        </span>
      )}

      {/* ── Model Picker ── */}
      <div className={vlStyles.settingRow}>
        <label className={vlStyles.settingLabel}>Model</label>
        <select
          className={vlStyles.modelSelect}
          value={selectedModel}
          onChange={(e) => handleModelSelect(e.target.value)}
          disabled={isTraining || isInitializing}
        >
          {SPEAKER_MODELS.map((m) => (
            <option key={m.key} value={m.key}>
              {m.name} — {m.size}
            </option>
          ))}
        </select>
      </div>

      {/* ── Threshold Slider ── */}
      <div className={vlStyles.settingRow}>
        <label className={vlStyles.settingLabel}>
          Match Threshold
          <span className={vlStyles.thresholdValue}>{threshold.toFixed(2)}</span>
        </label>
        <input
          type="range"
          className={vlStyles.thresholdSlider}
          min={THRESHOLD_MIN}
          max={THRESHOLD_MAX}
          step={THRESHOLD_STEP}
          value={threshold}
          onChange={(e) => setThreshold(parseFloat(e.target.value))}
          disabled={isTraining}
        />
        <div className={vlStyles.thresholdLabels}>
          <span>Lenient</span>
          <span>Strict</span>
        </div>
      </div>

      {/* ── Prep Screen — tips before recording starts ── */}
      {trainingPhase === 'prep' && (
        <div className={vlStyles.prepPanel}>
          <div className={vlStyles.prepTitle}>{pendingAdditive ? 'Add training data' : 'Before you start'}</div>
          <ul className={vlStyles.tipsList}>
            <li>🤫 Find a quiet spot — background noise hurts accuracy</li>
            <li>📱 Hold your phone at your normal distance from the mic</li>
            <li>🗣️ Speak naturally — don't whisper or shout</li>
            <li>📖 {pendingAdditive
              ? <>You'll read short sentences — training stops automatically when your voice matches</>
              : <>You'll read <strong>5</strong> short sentences out loud</>
            }</li>
            {pendingAdditive && (
              <li>💡 This adds to your existing profile — great for new headsets or environments</li>
            )}
          </ul>
          <button
            className={`${styles.speedBtn} ${vlStyles.startRecordingBtn}`}
            onClick={handleStartRecording}
            disabled={isInitializing}
            type="button"
          >
            {isInitializing ? '⏳ Loading…' : '🎙️ Start Recording'}
          </button>
          <button
            className={vlStyles.cancelPrepBtn}
            onClick={handleCancelPrep}
            type="button"
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Training Active — guided prompts, step dots, audio level ── */}
      {isTraining && (trainingPhase === 'recording' || trainingPhase === 'done') && (
        <div className={vlStyles.trainingPanel}>
          {/* Step indicator dots — only for fresh training (fixed count) */}
          {!isAdditiveTraining && (
            <div className={vlStyles.stepDots}>
              {Array.from({ length: requiredSamples }, (_, i) => (
                <div
                  key={i}
                  className={`${vlStyles.stepDot} ${
                    i < trainingSampleCount
                      ? vlStyles.stepDotFilled
                      : i === trainingSampleCount && !trainingComplete
                        ? vlStyles.stepDotCurrent
                        : ''
                  }`}
                />
              ))}
            </div>
          )}

          {/* Adaptive score indicator — only for additive training */}
          {isAdditiveTraining && adaptiveScore !== null && (
            <div className={vlStyles.adaptiveScoreRow}>
              <span className={vlStyles.adaptiveScoreLabel}>Match:</span>
              <span
                className={vlStyles.adaptiveScoreValue}
                style={{
                  color: adaptiveScore >= threshold
                    ? '#4caf50'
                    : adaptiveScore >= threshold - 0.1
                      ? '#ff9800'
                      : '#f44336',
                }}
              >
                {(adaptiveScore * 100).toFixed(0)}%
              </span>
              <div className={vlStyles.adaptiveScoreBar}>
                <div
                  className={vlStyles.adaptiveScoreFill}
                  style={{
                    width: `${Math.min(100, adaptiveScore * 100)}%`,
                    backgroundColor: adaptiveScore >= threshold
                      ? '#4caf50'
                      : adaptiveScore >= threshold - 0.1
                        ? '#ff9800'
                        : '#f44336',
                  }}
                />
                <div
                  className={vlStyles.adaptiveThresholdMark}
                  style={{ left: `${threshold * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Audio level indicator */}
          <div className={vlStyles.levelRow}>
            <div
              className={`${vlStyles.levelDot} ${audioLevel > 0.05 ? vlStyles.levelDotActive : ''}`}
              style={{
                transform: `scale(${1 + audioLevel * 0.8})`,
                opacity: 0.4 + audioLevel * 0.6,
              }}
            />
            <div className={vlStyles.levelBar}>
              <div
                className={vlStyles.levelFill}
                style={{ width: `${Math.min(100, audioLevel * 100)}%` }}
              />
            </div>
          </div>

          {/* Main content area — prompt or completion */}
          {trainingComplete ? (
            <>
              <div className={vlStyles.sampleCount}>
                <span className={vlStyles.sampleReady}>
                  {isAdditiveTraining
                    ? '✓ Profile looks good!'
                    : '✓ All samples captured!'}
                </span>
              </div>
              <div className={vlStyles.guidance}>
                Tap "Done Training" to save your voice profile
              </div>
            </>
          ) : (
            <>
              {/* "Got it!" flash or sentence prompt */}
              {showCaptured ? (
                <div className={vlStyles.sampleCaptured}>
                  {isAdditiveTraining && adaptiveScore !== null
                    ? `✓ Got it! (${(adaptiveScore * 100).toFixed(0)}%)`
                    : '✓ Got it!'}
                </div>
              ) : (
                <>
                  <div className={vlStyles.promptLabel}>
                    Read this aloud:
                  </div>
                  <div className={vlStyles.promptSentence}>
                    "{promptedSentences[trainingSampleCount % promptedSentences.length] ?? 'Say any sentence in your normal voice'}"
                  </div>
                </>
              )}

              {/* Adaptive progress message for additive training */}
              {isAdditiveTraining ? (
                <div className={vlStyles.sampleCount}>
                  {adaptiveScores.length === 0
                    ? 'Speak naturally — scoring will start after the first sample'
                    : adaptiveScores.length > 0 && adaptiveScore !== null && adaptiveScore >= threshold
                      ? 'Great match! A couple more to confirm…'
                      : adaptiveScores.length >= 2
                        ? 'Keep going — your voice profile is improving!'
                        : 'Almost there — keep reading…'}
                </div>
              ) : (
                <div className={vlStyles.sampleCount}>
                  {trainingSampleCount === 0
                    ? `Sample 1 of ${requiredSamples}`
                    : `Sample ${trainingSampleCount + 1} of ${requiredSamples}`}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Verification Phase ── */}
      {trainingPhase === 'verifying' && (
        <div className={vlStyles.verifyPanel}>
          <div className={vlStyles.verifyTitle}>🔍 Verifying…</div>
          <div className={vlStyles.promptLabel}>Read this aloud to verify:</div>
          <div className={vlStyles.promptSentence}>
            "{verificationSentence ?? 'Say any sentence in your normal voice'}"
          </div>
          {/* Audio level indicator */}
          <div className={vlStyles.levelRow}>
            <div
              className={`${vlStyles.levelDot} ${audioLevel > 0.05 ? vlStyles.levelDotActive : ''}`}
              style={{
                transform: `scale(${1 + audioLevel * 0.8})`,
                opacity: 0.4 + audioLevel * 0.6,
              }}
            />
            <div className={vlStyles.levelBar}>
              <div
                className={vlStyles.levelFill}
                style={{ width: `${Math.min(100, audioLevel * 100)}%` }}
              />
            </div>
          </div>
          <div className={vlStyles.guidance}>Listening for your voice…</div>
        </div>
      )}

      {trainingPhase === 'verified' && (
        <div className={vlStyles.verifyPanel + ' ' + vlStyles.verifySuccess}>
          <div className={vlStyles.verifyResult}>
            ✓ Verified! Your voice profile works.
          </div>
          {verificationScore !== null && (
            <div className={vlStyles.verifyScore}>
              Score: {verificationScore.toFixed(2)}
            </div>
          )}
        </div>
      )}

      {trainingPhase === 'verify-failed' && (
        <div className={vlStyles.verifyPanel + ' ' + vlStyles.verifyFail}>
          <div className={vlStyles.verifyResult + ' ' + vlStyles.verifyResultFail}>
            ⚠ Verification didn't pass — let's add a few more samples to strengthen your profile
          </div>
          {verificationScore !== null && (
            <div className={vlStyles.verifyScore}>
              Score: {verificationScore.toFixed(2)} (threshold: {threshold.toFixed(2)})
            </div>
          )}
          <button
            className={`${styles.speedBtn} ${vlStyles.retrainBtn}`}
            onClick={handleAdditiveTraining}
            type="button"
          >
            ➕ Add More Samples
          </button>
          <button
            className={vlStyles.startOverBtn}
            onClick={handleFullRetrain}
            type="button"
          >
            Start Over
          </button>
        </div>
      )}

      {/* Status — shown when NOT training and NOT in prep/verification */}
      {!isTraining && trainingPhase === 'idle' && (
        <span className={styles.apiKeyHint}>
          {isInitializing
            ? '⏳ Loading model…'
            : showSuccess
              ? '✓ Voice profile saved & verified'
              : trainingError
                ? trainingError
                : isEnrolled && trainedAt
                  ? `✓ Trained (${formatTimeAgo(trainedAt)})`
                  : 'Not trained'}
        </span>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        {isTraining ? (
          <button
            className={`${styles.speedBtn} ${trainingComplete ? vlStyles.doneBtn : ''}`}
            onClick={() => void stopTraining()}
            disabled={!trainingComplete}
            style={{
              flex: 1,
              fontSize: '13px',
              opacity: trainingComplete ? 1 : 0.4,
              cursor: trainingComplete ? 'pointer' : 'not-allowed',
            }}
            type="button"
          >
            {trainingComplete ? '✓ Done Training' : '🎙️ Recording…'}
          </button>
        ) : trainingPhase === 'prep' || trainingPhase === 'verifying' || trainingPhase === 'verified' || trainingPhase === 'verify-failed' ? (
          // Hide buttons during prep/verification — those panels have their own
          null
        ) : (
          <>
            <button
              className={styles.speedBtn}
              onClick={handleTrainClick}
              disabled={isInitializing}
              style={{ flex: 1, fontSize: '13px' }}
              type="button"
            >
              🎤 {isEnrolled ? 'Re-train' : 'Train Voice'}
            </button>
            {isEnrolled && (
              <button
                className={styles.speedBtn}
                onClick={handleAdditiveTrainClick}
                disabled={isInitializing}
                style={{ flex: 1, fontSize: '13px' }}
                type="button"
              >
                ➕ Add Training
              </button>
            )}
            <button
              className={styles.speedBtn}
              onClick={isEnrolled ? enterTestMode : undefined}
              disabled={!isEnrolled || isInitializing}
              style={{
                flex: 1,
                fontSize: '13px',
                opacity: !isEnrolled ? 0.4 : 1,
                cursor: !isEnrolled ? 'not-allowed' : 'pointer',
              }}
              type="button"
            >
              🧪 Test
            </button>
          </>
        )}
      </div>

      {/* Hint when test is disabled */}
      {!isTraining && trainingPhase === 'idle' && !isEnrolled && (
        <span className={vlStyles.disabledHint}>
          Train your voice first to enable testing
        </span>
      )}

      {/* Clear profile — only if enrolled and not training */}
      {isEnrolled && !isTraining && trainingPhase === 'idle' && (
        <button
          onClick={clearProfile}
          style={{
            marginTop: '8px',
            width: '100%',
            padding: '8px',
            background: 'rgba(255, 53, 53, 0.08)',
            border: '1px solid rgba(255, 53, 53, 0.25)',
            borderRadius: '10px',
            color: '#ff5555',
            fontSize: '12px',
            cursor: 'pointer',
          }}
          type="button"
        >
          Clear Profile
        </button>
      )}
    </div>
  );
}
